/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { FullModalLayout } from '@components/molecules'
import {
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	SelectChangeEvent,
	Autocomplete,
	TextField as MuiTextField,
} from '@mui/material'
import {
	useAppSelector,
	useAppDispatch,
	shipmentActions,
	productActions,
	customerActions,
	routeActions,
} from '@redux/index'
import { CustomerType, ProductType, DispatchCreationType } from '@schemas'
import { mono, boFont, boShared } from '@components/systems/backoffice/backoffice.tokens'
import {
	MANUAL_DISPATCH_KINDS,
	DISPATCH_CARRIER_OPTIONS,
} from '@src/configs/dispatch.config'
import { LIST_PATH } from './DispatchDetail'
import dayjs from 'dayjs'

interface LineDraft {
	productId: string
	freeName: string
	quantity: number
}

/**
 * 배송 건 추가 (`/main/shipping/new`). 주문 없이 손으로 적는 건이다: 도매 · 수리 · 광고 · 협찬 · 교체 ·
 * 지원 · 외주 · 입고 (DISPATCH_PLAN §3.2 · §3.7). 자사몰 판매만 주문확인 때 자동으로 생기니 여기서는 못 고른다.
 * 도매는 사이트 주문 없이 거래처에 보낸 것도 적어야 해서 수동으로도 만든다(2026-10-14 사용자 지시).
 *
 * 고객은 목록에서 고르거나(고객 ID 가 붙는다) 이름만 적는다. 품목 줄은 제품 또는 부품명과 수량이고,
 * 서버가 수량만큼 한 대씩 풀어 둔다(S/N 은 자세히 화면에서 채운다). 만들면 그 건의 자세히로 옮긴다.
 */
export const DispatchCreate = () => {
	const reduxDispatch = useAppDispatch()

	const products: ProductType[] = useAppSelector(state => state.product.products)
	const customers: CustomerType[] = useAppSelector(state => state.customer.customerList)
	const saving: boolean = useAppSelector(state => state.shipment.saving)
	const message: string = useAppSelector(state => state.shipment.message)

	React.useEffect(() => {
		if (products.length === 0)
			reduxDispatch(productActions.TRIGGER_INIT_PRODUCT_PAGE(true))
		if (customers.length === 0)
			reduxDispatch(customerActions.TRIGGER_INIT_CUSTOMER_PAGE(true))
	}, [products.length, customers.length, reduxDispatch])

	const [kind, setKind] = React.useState<string>('repair')
	const [customer, setCustomer] = React.useState<CustomerType | null>(null)
	const [partyName, setPartyName] = React.useState<string>('')
	const [orderedAt, setOrderedAt] = React.useState<string>(dayjs().format('YYYY-MM-DD'))
	const [carrier, setCarrier] = React.useState<string>('')
	const [trackingNumber, setTrackingNumber] = React.useState<string>('')
	const [memo, setMemo] = React.useState<string>('')
	const [receiverName, setReceiverName] = React.useState<string>('')
	const [receiverPhone, setReceiverPhone] = React.useState<string>('')
	const [address1, setAddress1] = React.useState<string>('')
	const [address2, setAddress2] = React.useState<string>('')
	const [city, setCity] = React.useState<string>('')
	const [stateName, setStateName] = React.useState<string>('')
	const [postalCode, setPostalCode] = React.useState<string>('')
	const [countryIso, setCountryIso] = React.useState<string>('KR')
	const [lines, setLines] = React.useState<LineDraft[]>([
		{ productId: '', freeName: '', quantity: 1 },
	])

	const onClose = React.useCallback(() => {
		if (saving) return
		reduxDispatch(routeActions.NAVIGATE_PATH(LIST_PATH))
	}, [saving, reduxDispatch])

	const onCustomerChange = (next: CustomerType | null) => {
		setCustomer(next)
		if (next !== null) {
			if (partyName.trim() === '') setPartyName(next.name || next.email)
			if (receiverName.trim() === '') setReceiverName(next.name)
		}
	}

	const validLines = lines.filter(
		l => (l.productId !== '' || l.freeName.trim() !== '') && l.quantity > 0,
	)
	const isValid: boolean =
		kind !== '' &&
		(partyName.trim() !== '' || receiverName.trim() !== '') &&
		validLines.length > 0

	const onSubmit = () => {
		if (!isValid) return
		const body: DispatchCreationType = {
			kind,
			customerId: customer?.id ?? '',
			partyName: partyName.trim(),
			orderedAt,
			carrier,
			trackingNumber: trackingNumber.trim(),
			shippedAt: trackingNumber.trim() !== '' ? dayjs().format('YYYY-MM-DD') : '',
			memo: memo.trim(),
			receiverName: receiverName.trim(),
			receiverPhone: receiverPhone.trim(),
			address1: address1.trim(),
			address2: address2.trim(),
			city: city.trim(),
			state: stateName.trim(),
			postalCode: postalCode.trim(),
			countryIso: countryIso.trim().toUpperCase(),
			lines: validLines.map(l => ({
				productId: l.productId,
				freeName: l.productId !== '' ? '' : l.freeName.trim(),
				quantity: Math.max(1, Math.min(100, Math.floor(l.quantity))),
				serials: [],
			})),
		}
		reduxDispatch(shipmentActions.TRIGGER_CREATE_DISPATCH(body))
	}

	return (
		<FullModalLayout
			overlay
			size="large"
			title="배송 건 추가"
			onCloseClick={onClose}
			customTitle={
				<View className="createHead" css={headStyle}>
					<span className="boKicker">배송 관리</span>
					<h1 className="createTitle">배송 건 추가</h1>
					<span className="createMeta">
						주문 없이 직접 적는 건이다. 도매 · 수리 · 광고 · 교체 · 지원 · 외주 · 입고에
						쓴다. 자사몰 판매는 주문확인 때 저절로 생긴다.
					</span>
				</View>
			}
		>
			<View css={style}>
				<View className="boSection">
					<Text className="boSectionTitle">기본 정보</Text>
					<View className="formRow three">
						<FormControl size="small">
							<InputLabel id="create-kind-select">종류</InputLabel>
							<Select
								labelId="create-kind-select"
								label="종류"
								value={kind}
								onChange={(e: SelectChangeEvent) => setKind(e.target.value)}
							>
								{MANUAL_DISPATCH_KINDS.map(k => (
									<MenuItem key={k.value} value={k.value}>
										{k.label}
										{k.direction === 'in' && (
											<span style={{ marginLeft: 8, fontSize: 11, color: mono.faint }}>
												들어오는 것
											</span>
										)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Autocomplete
							size="small"
							options={customers}
							value={customer}
							onChange={(_, next) => onCustomerChange(next)}
							getOptionLabel={o => (o.name !== '' ? `${o.name} · ${o.email}` : o.email)}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							renderInput={params => (
								<MuiTextField
									{...params}
									label="고객 계정(선택)"
									placeholder="이름 · 이메일"
								/>
							)}
						/>
						<MuiTextField
							label="거래처 / 고객 표시명"
							size="small"
							value={partyName}
							onChange={e => setPartyName(e.target.value)}
							placeholder="계정이 없으면 여기에 적는다"
						/>
					</View>
					<View className="formRow three">
						<MuiTextField
							label="주문일"
							size="small"
							type="date"
							value={orderedAt}
							onChange={e => setOrderedAt(e.target.value)}
							InputLabelProps={{ shrink: true }}
							helperText="목록의 월 필터가 이 날짜를 본다"
						/>
						<MuiTextField
							label="비고"
							size="small"
							value={memo}
							onChange={e => setMemo(e.target.value)}
						/>
						<View />
					</View>
				</View>

				<View className="boSection">
					<Text className="boSectionTitle">수취인</Text>
					<View className="formRow three">
						<MuiTextField
							label="수취인"
							size="small"
							value={receiverName}
							onChange={e => setReceiverName(e.target.value)}
						/>
						<MuiTextField
							label="전화"
							size="small"
							value={receiverPhone}
							onChange={e => setReceiverPhone(e.target.value)}
						/>
						<MuiTextField
							label="국가(ISO 2)"
							size="small"
							value={countryIso}
							onChange={e => setCountryIso(e.target.value.toUpperCase().slice(0, 2))}
						/>
					</View>
					<View className="formRow">
						<MuiTextField
							label="주소 1"
							size="small"
							value={address1}
							onChange={e => setAddress1(e.target.value)}
						/>
						<MuiTextField
							label="주소 2"
							size="small"
							value={address2}
							onChange={e => setAddress2(e.target.value)}
						/>
					</View>
					<View className="formRow three">
						<MuiTextField
							label="시"
							size="small"
							value={city}
							onChange={e => setCity(e.target.value)}
						/>
						<MuiTextField
							label="주 / 도"
							size="small"
							value={stateName}
							onChange={e => setStateName(e.target.value)}
						/>
						<MuiTextField
							label="우편번호"
							size="small"
							value={postalCode}
							onChange={e => setPostalCode(e.target.value)}
						/>
					</View>
				</View>

				<View className="boSection">
					<Text className="boSectionTitle">품목</Text>
					{lines.map((l, idx) => (
						<View className="formRow line" key={idx}>
							<FormControl size="small">
								<InputLabel id={`create-line-product-${idx}`}>제품</InputLabel>
								<Select
									labelId={`create-line-product-${idx}`}
									label="제품"
									value={l.productId}
									onChange={(e: SelectChangeEvent) =>
										setLines(prev =>
											prev.map((p, i) =>
												i === idx ? { ...p, productId: e.target.value } : p,
											),
										)
									}
								>
									<MenuItem value="">(부품명을 직접 적는다)</MenuItem>
									{products.map(p => (
										<MenuItem key={p.id} value={p.id}>
											{p.code} · {p.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<MuiTextField
								label="부품명"
								size="small"
								value={l.freeName}
								disabled={l.productId !== ''}
								onChange={e =>
									setLines(prev =>
										prev.map((p, i) =>
											i === idx ? { ...p, freeName: e.target.value } : p,
										),
									)
								}
								placeholder="PCB · TRS 케이블 …"
							/>
							<MuiTextField
								label="수량"
								size="small"
								type="number"
								value={l.quantity}
								onChange={e =>
									setLines(prev =>
										prev.map((p, i) =>
											i === idx ? { ...p, quantity: Number(e.target.value) } : p,
										),
									)
								}
							/>
							<button
								type="button"
								className="boBtnText"
								disabled={lines.length === 1}
								onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))}
							>
								빼기
							</button>
						</View>
					))}
					<button
						type="button"
						className="boBtnGhost"
						onClick={() =>
							setLines(prev => [...prev, { productId: '', freeName: '', quantity: 1 }])
						}
					>
						줄 추가
					</button>
					<Text className="boNote">
						수량만큼 한 대씩 줄이 생긴다. S/N 은 만든 뒤 자세히 화면에서 채운다.
					</Text>
				</View>

				<View className="boSection">
					<Text className="boSectionTitle">배송</Text>
					<View className="formRow three">
						<FormControl size="small">
							<InputLabel id="create-carrier-select" shrink>
								배송사
							</InputLabel>
							<Select
								labelId="create-carrier-select"
								label="배송사"
								value={carrier}
								displayEmpty
								onChange={(e: SelectChangeEvent) => setCarrier(e.target.value)}
							>
								<MenuItem value="">(아직 없음)</MenuItem>
								{DISPATCH_CARRIER_OPTIONS.map(c => (
									<MenuItem key={c.value} value={c.value}>
										{c.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<MuiTextField
							label="송장"
							size="small"
							value={trackingNumber}
							onChange={e => setTrackingNumber(e.target.value)}
							helperText="FedEx 라벨은 만든 뒤 자세히 화면에서 발급한다"
						/>
						<View />
					</View>
				</View>

				{message !== '' && <Text className="boError">{message}</Text>}

				<View className="actionBar">
					<Text className="boNote">
						{isValid ? '' : '종류, 거래처나 수취인, 품목 한 줄은 있어야 한다.'}
					</Text>
					<View className="actionRight">
						<button
							type="button"
							className="boBtnGhost"
							onClick={onClose}
							disabled={saving}
						>
							취소
						</button>
						<button
							type="button"
							className="boBtn"
							onClick={onSubmit}
							disabled={!isValid || saving}
						>
							{saving ? '추가 중…' : '배송 건 추가'}
						</button>
					</View>
				</View>
			</View>
		</FullModalLayout>
	)
}

const headStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	fontFamily: boFont.ui,

	'.boKicker': {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	'.createTitle': {
		margin: 0,
		fontSize: 'clamp(24px, 2.6vw, 32px)',
		fontWeight: 200,
		lineHeight: 1.1,
		color: mono.ink,
	},
	'.createMeta': { fontSize: 13, fontWeight: 300, color: mono.dim },
})

const style = css({
	...boShared,
	paddingTop: 8,

	'.boSection': {
		...boShared['.boSection'],
		'.boSectionTitle': { marginBottom: 8 },
	},
	'.boNote': { fontSize: 12, color: mono.dim, marginTop: 8, lineHeight: 1.6 },

	// 폼은 넓은 화면에서도 읽는 폭을 지킨다(라벨 생성과 같은 규칙)
	'.boSection, .actionBar': { maxWidth: 960 },

	'.formRow': {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
		marginBottom: 12,
		'&.three': { gridTemplateColumns: 'repeat(3, 1fr)' },
		'&.line': { gridTemplateColumns: '2fr 2fr 1fr auto', alignItems: 'center' },
	},

	'.actionBar': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		paddingTop: 20,
		borderTop: `1px solid ${mono.line}`,
		'.actionRight': { display: 'flex', alignItems: 'center', gap: 8 },
	},
})
